import { Injectable } from '@angular/core';
import { HttpService } from '../http.service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Board,  BoardTagetTimeline,  BoardTicket } from '../../_pages/boards/boards.page';
import { HttpFunctionResult } from '../../_interfaces/HttpFunctionResult';
import { BoardSelectDate } from '../../_pages/boards/board-time-select/board-time-select.component';
import { Employee } from '../../_interfaces/Employee';
import { StorageManagerService } from '../storage-manager.service';
import { DarkmodeService } from '../darkmode.service';
import { TicketEditService } from '../ticket-edit.service';
import { BoardCardView } from '../../_pages/boards/board-card-view-editor/board-card-view-editor.component';

export interface TicketTag{
  id: number,
  description: string,
  color?: string,
  checked?: boolean
}

export interface TicketTagColored{
  id: number
  description: string
  rgba: string
  hex: string
}

export interface BoardTargetTimeResponse{
  statusId: number
  ticketId: number
  targetTime: number
  currentTime: number
  workstepId: number
}

export enum BoardEditorFilter{
  ALL = "all",
  ONLYEDITOR = "onlyEditor",
  ONLYVIEWER = "onlyViewer"
}

@Injectable({
  providedIn: 'root'
})
export class BoardService {

  private _boards: Board[] = []
  private _tags: TicketTag[] = []
  private _employees: Employee[] = []
  public _coloredBoards: boolean = false

  public boardView: BoardCardView[] = [
    {name: "Tags", active: true},
    {name: "Priorität", active: true},
    {name: "Ticketnummer", active: true},
    {name: "Kunde", active: true},
    {name: "Tätigkeiten", active: true},
    {name: "Mitarbeiter", active: true},
    {name: "Anhänge", active: true},
    {name: "Soll/Ist-Zeit", active: true},
  ]

  public selectedEditorFilter: BoardEditorFilter = BoardEditorFilter.ALL

  constructor(
    private http: HttpService,
    private storageManager: StorageManagerService,
    private ticketEditSrv: TicketEditService,
    private darkmode: DarkmodeService
  ) { }

  public get tags(): TicketTag[]{
    return this._tags
  }

  public set tags(tags: TicketTag[]){
    this._tags = tags
  }

  public get boards(): Board[]{
    return this._boards
  }

  public get employees(): Employee[]{
    return this._employees
  }

  public set employees(employees: Employee[]){
    this._employees = employees
  }

  public get someBoardViewDisabled(): boolean{
    return this.boardView.some(bv => !bv.active)
  }

  public get coloredBoards(): boolean{
    return this._coloredBoards
  }

  public set coloredBoards(colored: boolean){
    this._coloredBoards = colored
  }

  /**
   * gibt alle Boards mit den Tickets zurück
   * @param {BoardSelectDate} selectedDate Zeitraum für die Tickets
   * @returns 
   */
  public async loadBoards(selectedDate: BoardSelectDate, searchText: string, hiddenBoardIds: number[]): Promise<HttpFunctionResult<any>>{
    const tags: string = this._tags.filter(t =>t.checked).map(t => t.id).join(",")
    const userIds: string =  this._employees.filter(e => e.checked).map(e => e.id).join(",")

    const result: HttpFunctionResult<Board[]> = await this.http.GET<Board[]>({
      path: `ticket/board?selectedDate=${selectedDate.value=="custom" ? selectedDate.timePeriodString : selectedDate.value}&tags=${tags}&userIds=${userIds}&searchText=${searchText}&editfilter=${this.selectedEditorFilter}`
    })
    if(!result.success) return {success: false}

    this._boards = result.data

    hiddenBoardIds.forEach(hb => {
      const board = this._boards.find(b => b.id == hb)
      if(board) board.hidden = true
    })
    this._boards.forEach(b => {
      b.tickets.forEach(t => {
        t.coloredTags = this.tags.filter(tag => t.tags.includes(tag.id)).map(tag => {
          const rgb: {r: number, g:number, b: number} = this.darkmode.hexToRgb(tag?.color)
          return {
            id: tag.id,
            description: tag.description,
            rgba: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
            hex: tag.color
          }
        })

        t.timeline = new BoardTagetTimeline(0, 0, t.targetTimeWorkstepId)

      });
    })
    this.loadAllTargetTimes(this._boards)
    return result
  }

  /**
   * Ruft die nächsten Tickets für das angegebene Board ab.
   * @param board - Das Board, für das die Tickets abgerufen werden sollen.
   * @param selectedDate - Das ausgewählte Datum oder der ausgewählte Zeitraum.
   * @param searchText - Der Text, nach dem gesucht werden soll.
   * @returns Ein Promise, das ein Objekt mit dem Schlüssel "success" und dem Wert "true" zurückgibt, wenn die Anfrage erfolgreich war.
   */
  public async getOffset(board: Board, selectedDate: BoardSelectDate, searchText: string){
    const tags: string = this._tags.filter(t =>t.checked).map(t => t.id).join(",")
    const userIds: string =  this._employees.filter(e => e.checked).map(e => e.id).join(",")

    const result: HttpFunctionResult<BoardTicket[]> = await this.http.GET<BoardTicket[]>({
      path: `ticket/board/offset?boardId=${board.id}&selectedDate=${selectedDate.value=="custom" ? selectedDate.timePeriodString : selectedDate.value}&offset=${board.tickets.length}&tags=${tags}&userIds=${userIds}&searchText=${searchText}`
    })
    if(!result.success) return {success: false}

    if(result.data.length == 0) board.hasNotMoreTickets = true
    result.data.forEach(t => {
      t.coloredTags = this.tags.filter(tag => t.tags.includes(tag.id)).map(tag => {
        const rgb: {r: number, g:number, b: number} = this.darkmode.hexToRgb(tag?.color)
        return {
          id: tag.id,
          description: tag.description,
          rgba: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
          hex: tag.color
        }
      })

      t.timeline = new BoardTagetTimeline(0, 0, t.targetTimeWorkstepId)
    })
    board.tickets = [...board.tickets, ...result.data as BoardTicket[]]
    this.loadAllTargetTimes([{...board, tickets: result.data}]) 
    return result
  }

  /**
   * Lädt die Mitarbeiterdaten aus dem Backend.
   * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit dem Erfolg der Operation zurückgibt.
   */
  public async loadEmployees(): Promise<HttpFunctionResult<any>>{
    const result: HttpFunctionResult<any> = await this.http.GET<any>({
      path: `ticket/editinfo/employees`
    })
    if(!result.success) return {success: false}
    this.employees = result.data[1].map(data => ({id: data.id, firstName: data?.firstName, lastName: data?.lastName, checked: false}))
    return result
  }

  /**
   * Updatet die Position eines Tickets
   * @param {number} ticketId Id des Tickets
   * @param {number} position neue Position
   * @returns 
   */
  public async updateTicketPosition(ticketId: number, position: number, status: number): Promise<HttpFunctionResult<any>>{
    const isClosed: boolean = this.ticketEditSrv.ticketStates.find(ts => ts.id == status)?.closed
    return await this.http.PUT<any, any>({
      path: `ticket/board/item/position?ticketId=${ticketId}&newPosition=${position}&status=${status}&closed=${isClosed}`,
      body: {}
    })
  }

  /**
   * Holt alle Tags vom Server
   * @returns 
   */
  public async loadTags(): Promise<HttpFunctionResult<any>>{
    const result: HttpFunctionResult<TicketTag[]> = await this.http.GET<TicketTag[]>({path: "ticket/board/tags"})
    if(!result.success) return {success: false}
    this.tags = result.data
    return result
  }

  /**
   * Ordnet die Boards an eine neue Stelle
   * @returns 
   */
  public async reorderBoards(): Promise<HttpFunctionResult<any>>{
    let body: any = {}
    this._boards.forEach(b => {
      const key: string = b.id.toString()
      body = {...body, ...{[key]: this._boards.indexOf(b) + 1}}
    })
    return await this.http.PUT<any, any>({
      path: "ticket/board/reorder",
      body: body
    })
  }

  public async loadAllTargetTimes(boards: Board[]){

    if(this.boardView[7]?.active == false) return
    

    const ticketIds = boards.map(b => 
      b.tickets.filter(t => t.timeline?.hasTargetTime)
      .map(t => ({
        boardId: b.id, 
        workstepId: t.targetTimeWorkstepId,
        ticketId: t.id
      }))
    ).reduce((acc, val) => acc.concat(val), [])

    if(ticketIds.length == 0) return

    const requests: Promise<HttpFunctionResult<BoardTargetTimeResponse>>[] = ticketIds.map(t => this.http.GET<any>({
      path: `ticket/board/targettime/${t.workstepId}?statusId=${t.boardId}&ticketId=${t.ticketId}` 
    }))
    
    requests.forEach(async request => {
      const result: HttpFunctionResult<BoardTargetTimeResponse> = await request
      if(!result.success) return
      
      const board = this.boards.find(b => b.id == result.data.statusId)
      if(!board) return

      const ticket = board.tickets.find(t => t.id == result.data.ticketId)
      if(!ticket) return

      ticket.timeline = new BoardTagetTimeline(result.data.targetTime, result.data.currentTime, result.data.workstepId)
    })

  }

}
